import UploadBox from '@/components/UploadBox/UploadBox';
import { useGetExpressAvatarUserCapping } from '@/pages/avatar';
import theme from '@/theme';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { ReactNode } from 'react';

export interface CardInterface {
    id: string;
    testId?: string;
    image: string;
    video: string;
    title: ReactNode;
    description: string;
    quality?: string;
    onClick: (file: React.MouseEvent<HTMLDivElement, MouseEvent> | File) => Promise<void>;
    onGetFile?: any;
    isLoading?: boolean;
    isDisabled?: boolean;
}

interface Props {
    card: CardInterface;
    id: string;
}

import React, { useState } from 'react';

const StyledVideo = styled.video`
    width: 100%;
    height: 220px;
    object-fit: cover;
    border-radius: 16px 16px 0px 0px;
    position: absolute;
`;

const RemainingBox = styled.div`
    position: absolute;
    top: 11px;
    left: 11px;
    z-index: 10;
    display: flex;
    padding: 8px;
    width: auto;
    height: auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 8px;
    background: var(--Blacks-White, #fff);
    backdrop-filter: blur(12px);
`;

const RemainingText = styled.span<{ isZero: boolean }>`
    color: ${({ isZero }) => (isZero ? 'red' : 'var(--Blacks-700, #5C5C5C)')};
    font-family: 'Manrope', sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 8px;
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: 220px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    border-radius: 16px 16px 0px 0px;
    overflow: hidden;
`;

const StyledImage = styled.img`
    width: 100%;
    height: 220px;
    object-fit: cover;
    border-radius: 16px 16px 0px 0px;
    position: absolute;
`;

const StyledCard = styled.div<{ isDisabled: boolean }>`
    border-radius: 20px;
    border: 1px solid ${theme.colors.black[300]};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    font-family: Manrope;
    color: ${theme.colors.black[900]};
    width: 308px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    margin-block-end: 24px;

    :hover {
        box-shadow:
            0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.12),
            1.1px 2.8px 3.9px -0.4px rgba(187, 187, 187, 0.17),
            2.4px 6.1px 8.6px -0.8px rgba(187, 187, 187, 0.23),
            5.3px 13.3px 18.8px -1.2px rgba(187, 187, 187, 0.29);
    }
`;

const InfoContainer = styled.div`
    width: 100%;
    padding: 16px 24px;
`;

const TitleContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    margin-bottom: 8px;
`;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.4px;
    text-wrap: nowrap;
`;

const Description = styled.p`
    color: ${theme.colors.black[700]};
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0.15px;
    margin-bottom: 16px;
`;

const Quality = styled.span`
    display: flex;
    padding: 6px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    background: ${theme.colors.primary.bgLight};
    backdrop-filter: blur(12px);
    color: ${theme.colors.primary.main};
    font-family: Manrope;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 8px;
    text-wrap: nowrap;
`;

export const CreateAvatarCard = ({ card, id }: Props) => {
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const { data } = useGetExpressAvatarUserCapping();

    const handleMouseEnter = () => {
        setIsVideoPlaying(true);
    };

    const handleMouseLeave = () => {
        setIsVideoPlaying(false);
    };

    let count = data?.remaining ?? 0;
    const unlimited = data ? data.unlimited : false;
    return (
        <StyledCard
            id={id}
            onClick={card.isDisabled ? undefined : card.onClick}
            isDisabled={!!card.isDisabled}
            data-testid={card.testId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            {card.isLoading ? (
                <CircularProgress size={40} />
            ) : (
                <>
                    {card.onGetFile ? (
                        <UploadBox
                            uploadBoxBorderRadius={'16px 16px 0px 0px'}
                            dropzoneBorderRadius={'16px'}
                            accept={{ 'image/*': [] }}
                            onInputChange={async file => card.onGetFile(file as File)}>
                            <ImagePlaceholder>
                                {isVideoPlaying ? (
                                    <StyledVideo src={card.video} poster={card.image} loop muted autoPlay />
                                ) : (
                                    <StyledImage src={card.image} alt="Card Image" />
                                )}
                            </ImagePlaceholder>
                        </UploadBox>
                    ) : (
                        <ImagePlaceholder>
                            {!unlimited && data && (
                                <RemainingBox>
                                    <RemainingText isZero={count === 0}>{count || 0} Remaining</RemainingText>
                                </RemainingBox>
                            )}
                            {isVideoPlaying ? (
                                <StyledVideo src={card.video} poster={card.image} loop muted autoPlay />
                            ) : (
                                <StyledImage src={card.image} alt="Card Image" />
                            )}
                        </ImagePlaceholder>
                    )}
                    <InfoContainer>
                        <TitleContainer>
                            <Title>{card.title}</Title>
                            {card.quality && <Quality>{card.quality}</Quality>}
                        </TitleContainer>
                        <Description>{card.description}</Description>
                    </InfoContainer>
                </>
            )}
        </StyledCard>
    );
};
